import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getActiveReferral, clearReferral } from '../lib/referral';
import { getActivePartnerCode, clearPartnerCode } from '../lib/partner';
import { getAttribution, clearAttribution } from '../lib/attribution';
import { setSentryUser } from '../lib/sentry';
import { CURRENT_TERMS_VERSION } from '../lib/legal';
import { normalizeBrPhone } from '../lib/whatsappOutreach';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Listen to Firebase Auth state ──────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const extra   = userDoc.exists() ? userDoc.data() : {};
          setUser({
            id:    firebaseUser.uid,
            uid:   firebaseUser.uid,
            email: firebaseUser.email,
            name:  firebaseUser.displayName || extra.name || '',
            plan:  extra.plan || 'basic',
            ...extra,
          });
        } catch {
          setUser({
            id:    firebaseUser.uid,
            uid:   firebaseUser.uid,
            email: firebaseUser.email,
            name:  firebaseUser.displayName || '',
            plan:  'basic',
          });
        }
        setSentryUser({ uid: firebaseUser.uid, email: firebaseUser.email });
      } else {
        setUser(null);
        setSentryUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const signup = async (email, password, name, phone = '') => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: name });

    // Garante que o token de auth está pronto para o Firestore antes de
    // qualquer próxima escrita (evita race no fluxo signup → onboarding).
    try { await result.user.getIdToken(true); } catch { /* token may be eventual */ }

    // Captura referral salvo no localStorage (caso a pessoa tenha entrado
    // por link tipo /?ref=XYZ). Será validado e creditado no servidor
    // quando o casamento receber o 1º presente pago.
    const referralCode = getActiveReferral();
    // Captura código de parceiro (?parc=XYZ — influencer/parceria B2B).
    // Programa separado do referral acima: comissão recorrente sobre todo o
    // faturamento do casal, não só o 1º presente. Validado no servidor.
    const partnerCode = getActivePartnerCode();
    // Origem do cadastro (utm/site de referência) — só métrica; getAttribution
    // não lança e o campo é opcional, então nunca atrapalha o signup.
    const attribution = getAttribution({ partnerCode: partnerCode || '' });

    // Aguarda a escrita do users doc antes de devolver o controle — assim
    // o onboarding nunca chama completeOnboarding antes do users existir.
    try {
      await setDoc(doc(db, 'users', result.user.uid), {
        name,
        email,
        // Só dígitos com DDI (55…); '' se não informado.
        ...(normalizeBrPhone(phone) ? { phone: normalizeBrPhone(phone) } : {}),
        plan:      'basic',
        onboardingCompleted: false,
        ...(referralCode ? { referredByCode: referralCode } : {}),
        ...(partnerCode ? { partnerCode } : {}),
        attribution,
        // Registro de aceite de Termos + Privacidade — pré-requisito do
        // signup. Guardamos timestamp + versão dos documentos legais e
        // user agent (best-effort) pra auditoria/disputa LGPD.
        termsAcceptedAt: serverTimestamp(),
        termsVersion: CURRENT_TERMS_VERSION,
        termsAcceptedUserAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 300) : '',
        createdAt: serverTimestamp(),
      });
      // Consumimos o ref apenas após gravar o user doc com sucesso. Se o
      // signup falhar, mantemos pra tentar de novo no próximo signup.
      if (referralCode) clearReferral();
      if (partnerCode) clearPartnerCode();
      clearAttribution();
    } catch (err) {
      // Não bloqueia o signup, mas registra — o onboarding tem retry próprio.
      console.warn('signup: falha ao criar users doc:', err?.message || err);
    }

    setUser({
      id:    result.user.uid,
      uid:   result.user.uid,
      email: result.user.email,
      name,
      plan:  'basic',
    });
    setLoading(false);

    return result.user;
  };

  const logout = () => signOut(auth);

  // E-mail próprio (com a marca do Mimo do Casal) via Cloud Function, em vez
  // do template genérico do Firebase Auth (sendPasswordResetEmail nativo).
  const resetPassword = async (email) => {
    const resp = await fetch('/api/sendPasswordReset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      const error = new Error(data?.error?.message || 'Não foi possível enviar o e-mail agora.');
      error.code = data?.error?.code;
      throw error;
    }
  };

  const updateUser = async (updates) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.id), updates);
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateUser, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
